import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  AppApi,
  DpBiDefaults,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import {
  addOrUpdateRouterWithPattern,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
  getCollectionType,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import {
  CATEGORY_CONSUMER_FIELD_PLUGIN_ID,
  FieldType,
} from '@wix/wix-data-schema-types'
import type {
  FieldSpec,
  MultiReferenceField as SchemaMultiReferenceField,
  Schema,
} from '@wix/wix-data-schema-types'
import type Experiments from '@wix/wix-experiments'
import { getProgressBar } from '../../progress-bar/executeSteps'
import { afterAddingPages as addPageLinkFields } from '../add-dynamic-pages/dpCreationSteps'
import { ensurePageIsLegacyIndexable } from '../ensurePageIsLegacyIndexable'
import {
  bindCategoryDatasets,
  type MultiReferenceField,
} from './bindCategoryDatasets'
import { duplicateRepeaterSections } from './duplicateRepeaterSections'
import { prepareDataForCategoryPage } from './prepareDataForCategoryPage'

const isCategoryConsumerField = (
  field: FieldSpec,
): field is FieldSpec & SchemaMultiReferenceField =>
  field.type === FieldType.multiReference &&
  !!field.plugins?.[CATEGORY_CONSUMER_FIELD_PLUGIN_ID]

const findCategoryConsumerFields = (
  fields: Schema['fields'],
): MultiReferenceField[] =>
  Object.values(fields)
    .filter(isCategoryConsumerField)
    .map(field => ({
      referencedCollectionId: field.referencedCollection,
      referencingFieldKey: field.referencingFieldKey,
    }))

const findPlainMultiReferenceFields = (
  fields: Schema['fields'],
): MultiReferenceField[] =>
  Object.values(fields)
    .filter(
      (field): field is FieldSpec & SchemaMultiReferenceField =>
        field.type === FieldType.multiReference &&
        !field.plugins?.[CATEGORY_CONSUMER_FIELD_PLUGIN_ID],
    )
    .map(field => ({
      referencedCollectionId: field.referencedCollection,
      referencingFieldKey: field.referencingFieldKey,
    }))

const interactionName = 'addCategoryPage'

export const addCategoryPage = async ({
  editorSdkProxy,
  collectionsApi,
  appApi,
  experimentsManager,
  i18n,
  logger,
  fedopsLogger,
  editorType,
  collectionId,
  biDefaults,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  appApi: AppApi
  experimentsManager: Experiments
  i18n: I18n
  logger: Logger
  fedopsLogger: FedopsLogger
  editorType: EDITOR_TYPE
  collectionId: string
  biDefaults: DpBiDefaults
}) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 1,
    title: i18n.t('Adding_Dynamic_Pages_Native_Progress_Bar_Title'),
  })

  await progressBar.open()

  try {
    const schema = await collectionsApi.get(collectionId)
    const categoryConsumerFields = findCategoryConsumerFields(schema.fields)
    const fieldsForItemsDataset =
      categoryConsumerFields.length > 0
        ? categoryConsumerFields
        : experimentsManager.enabled(
              'specs.wixDataClient.CategoryPageMultiRefFallback',
            )
          ? findPlainMultiReferenceFields(schema.fields)
          : []

    const [
      {
        routeDefinition,
        pageTitle,
        routerPrefix,
        pageLinks,
        pattern,
        presetPageDefinition,
        presetCategoryCollectionName,
      },
      referencedSchemas,
    ] = await Promise.all([
      prepareDataForCategoryPage({
        editorSdkProxy,
        appApi,
        schema,
        experimentsManager,
        editorType,
      }),
      Promise.all(
        fieldsForItemsDataset.map(({ referencedCollectionId }) =>
          collectionsApi.get(referencedCollectionId),
        ),
      ),
    ])

    await progressBar.update({
      stepTitle: i18n.t(
        'Adding_Dynamic_Pages_Native_Progress_Bar_Category_Page_Step_Title',
      ),
    })

    const pageRef = await runTransactionWithRetry(editorSdkProxy, async () => {
      const routerRef = await addOrUpdateRouterWithPattern(
        editorSdkProxy,
        routerPrefix,
        pattern,
        routeDefinition,
      )

      const pageRef = await editorSdkProxy.document.pages.add({
        title: pageTitle,
        definition: presetPageDefinition.definition,
        shouldAddMenuItem: true,
        shouldNavigateToPage: false,
      })

      await editorSdkProxy.routers.pages.connect({
        routerRef,
        pageRef,
        pageRoles: [routeDefinition.pageRole],
      })

      await addPageLinkFields({
        editorSdkProxy,
        collectionsApi,
        collectionId,
        pageLinkFieldsToAdd: [
          {
            fieldKey: pageLinks.itemPage.pageLinkFieldKey,
            pattern,
            routerPrefix,
            displayName: pageTitle,
            pageId: pageRef.id,
          },
        ],
      })

      await ensurePageIsLegacyIndexable({ editorSdkProxy, pageRef })

      const regularDatasetRef = await bindCategoryDatasets({
        appApi,
        editorSdkProxy,
        pageRef,
        schema,
        categoryConsumerFields: fieldsForItemsDataset,
        referencedSchemas,
        presetCategoryCollectionName,
        pageLinkFieldKey: pageLinks.itemPage.pageLinkFieldKey,
        editorType,
      })

      if (fieldsForItemsDataset.length > 1 && regularDatasetRef) {
        await duplicateRepeaterSections({
          appApi,
          editorSdkProxy,
          pageRef,
          regularDatasetRef,
          additionalCategoryConsumerFields: fieldsForItemsDataset.slice(1),
          additionalReferencedSchemas: referencedSchemas.slice(1),
        })
      }

      return pageRef
    })

    await editorSdkProxy.history.add({
      label: UNDO_REDO_LABELS.ADD_DATA_BINDING_URL,
    })

    await progressBar.update()

    logger.bi(
      dynamicPageCustomRouterCreatePageClicked({
        page_type: 'category_item_page',
        collection_id: collectionId,
        new_url: `${routerPrefix}${pattern}`,
        is_auto_bound: true,
        new_collection: false,
        pageId: pageRef.id,
        action_type: 'add_dynamic_page',
        template_id: 'category',
        collection_type: getCollectionType(schema),
        ...biDefaults,
      }),
    )

    fedopsLogger.interactionEnded(interactionName)

    await editorSdkProxy.pages.navigateTo({ pageRef })

    return { pageRefs: [pageRef] }
  } catch (error: unknown) {
    const wrappedError = new Error('Failed to add category page', {
      cause: error,
    })
    logger.error(wrappedError, { collectionId })
    throw wrappedError
  } finally {
    await progressBar.close()
  }
}
